import { normalizeError } from "@/lib/errorUtils";
import { supabase } from "@/lib/supabase";

export type CreditCardCommandInvoice = {
  id: string;
  user_id: string;
  card_id: string;
  invoice_month: string;
  closing_date: string;
  due_date: string;
  total_amount: number | string;
  status: "open" | "closed" | "paid" | "overdue";
  paid_at: string | null;
  paid_from_account_id: string | null;
  paid_amount?: number | string | null;
  paid_transaction_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CreditCardCommandPayment = {
  id: string;
  user_id: string;
  invoice_id: string;
  amount: number | string;
  payment_date: string;
  account_id: string | null;
  effective_at: string | null;
  notes: string | null;
  created_by: string;
  status: "pending" | "completed" | "reversed" | "failed" | "cancelled";
  origin: "account" | "manual" | "import" | "legacy" | "adjustment";
  transaction_id: string | null;
  idempotency_key: string;
  reversed_at?: string | null;
  cancelled_at?: string | null;
  reversal_transaction_id?: string | null;
  created_at: string;
  updated_at: string;
};

type CreditCardCommandAccountResult = {
  id: string;
  current_balance: number | string;
};

export type RegisterInvoicePaymentInput = {
  invoiceId: string;
  amount: number;
  accountId?: string | null;
  paymentDate: string;
  /** Deprecated compatibility argument. Immediate execution time is server-owned. */
  effectiveAt?: string | null;
  notes?: string | null;
  idempotencyKey: string;
};

export type ReverseInvoicePaymentInput = {
  paymentId: string;
  reversalDate: string;
  reversedAt: string;
  idempotencyKey: string;
};

export type CancelInvoicePaymentInput = {
  paymentId: string;
  cancelledAt: string;
  idempotencyKey: string;
};

export type ReopenInvoiceInput = {
  invoiceId: string;
  reopenedAt: string;
  idempotencyKey: string;
};

export type RegisterInvoicePaymentResult = {
  invoice: CreditCardCommandInvoice;
  payment: CreditCardCommandPayment;
  account: CreditCardCommandAccountResult | null;
  transaction_id: string | null;
  idempotent: boolean;
};

export type ReverseInvoicePaymentResult = {
  invoice: CreditCardCommandInvoice;
  payment: CreditCardCommandPayment;
  account: CreditCardCommandAccountResult | null;
  reversal_transaction_id: string | null;
  idempotent: boolean;
};

export type CancelInvoicePaymentResult = {
  invoice: CreditCardCommandInvoice;
  payment: CreditCardCommandPayment;
  idempotent: boolean;
};

export type ReopenInvoiceResult = {
  invoice: CreditCardCommandInvoice;
  reversed_payment_count: number;
  accounts: CreditCardCommandAccountResult[];
  idempotent: boolean;
};

type RpcResult<T> = {
  data: T | null;
  error: unknown;
};

type CreditCardCommandRpcClient = {
  rpc(name: string, params: Record<string, unknown>): PromiseLike<RpcResult<unknown>>;
};

function normalizeMoney(value: number) {
  return Math.round(value * 100) / 100;
}

function sanitizeKeyPart(value: string | number | null | undefined) {
  return String(value ?? "none")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, "-");
}

export function buildCreditCardCommandIdempotencyKey(
  command: "payment" | "reverse" | "cancel" | "reopen",
  parts: Array<string | number | null | undefined>
) {
  const key = ["credit-card-v2", command, ...parts.map(sanitizeKeyPart)].join(":");
  return key.slice(0, 240);
}

function commandError(error: unknown, operation: string) {
  return normalizeError(error, `credit_card_${operation}_command_failed`);
}

async function executeCommand<T>(
  client: CreditCardCommandRpcClient,
  operation: string,
  rpcName: string,
  params: Record<string, unknown>
) {
  const startedAt = performance.now();
  const { data, error } = (await client.rpc(rpcName, params)) as RpcResult<T>;

  if (error || !data) {
    if (import.meta.env.DEV) {
      console.info("[credit-card-command]", {
        operation,
        outcome: "failed",
        durationMs: Math.round(performance.now() - startedAt),
      });
    }
    throw commandError(error, operation);
  }

  if (import.meta.env.DEV) {
    const result = data as T & { idempotent?: boolean };
    console.info("[credit-card-command]", {
      operation,
      outcome: "committed",
      idempotent: Boolean(result.idempotent),
      durationMs: Math.round(performance.now() - startedAt),
    });
  }

  return data;
}

export function createCreditCardCommandService(
  client: CreditCardCommandRpcClient = supabase as unknown as CreditCardCommandRpcClient
) {
  return {
    registerInvoicePayment(input: RegisterInvoicePaymentInput) {
      return executeCommand<RegisterInvoicePaymentResult>(
        client,
        "register_payment",
        "register_credit_card_invoice_payment",
        {
          p_invoice_id: input.invoiceId,
          p_amount: normalizeMoney(input.amount),
          p_account_id: input.accountId ?? null,
          p_payment_date: input.paymentDate,
          p_effective_at: input.effectiveAt ?? null,
          p_notes: input.notes?.trim() || null,
          p_idempotency_key: input.idempotencyKey,
        }
      );
    },

    reverseInvoicePayment(input: ReverseInvoicePaymentInput) {
      return executeCommand<ReverseInvoicePaymentResult>(
        client,
        "reverse_payment",
        "reverse_credit_card_invoice_payment",
        {
          p_payment_id: input.paymentId,
          p_reversal_date: input.reversalDate,
          p_reversed_at: input.reversedAt,
          p_idempotency_key: input.idempotencyKey,
        }
      );
    },

    cancelInvoicePayment(input: CancelInvoicePaymentInput) {
      return executeCommand<CancelInvoicePaymentResult>(
        client,
        "cancel_payment",
        "cancel_credit_card_invoice_payment",
        {
          p_payment_id: input.paymentId,
          p_cancelled_at: input.cancelledAt,
          p_idempotency_key: input.idempotencyKey,
        }
      );
    },

    reopenInvoice(input: ReopenInvoiceInput) {
      return executeCommand<ReopenInvoiceResult>(
        client,
        "reopen_invoice",
        "reopen_credit_card_invoice",
        {
          p_invoice_id: input.invoiceId,
          p_reopened_at: input.reopenedAt,
          p_idempotency_key: input.idempotencyKey,
        }
      );
    },
  } as const;
}

export const CreditCardCommandService = createCreditCardCommandService();
